import { Table } from 'rsuite';
import React, { useState } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput } from '../../../../api/trpc';
import { ModalsService } from '../../../../services/modals.service';
import { CustomsDeliveryTrueSignEditModal } from './customs-true-sign-edit-modal';
import { CellProps } from 'rsuite/Table';
import {
  CleanCustomsDeliveryDuty,
  CleanCustomsDeliveryTrueSignTariff,
  RbacObject,
} from '../../../../export-types/cleaned-types';
import { getCurrencySymbol, toMoney } from '../../../../utils/common.utils';
import { Rbac, useRbac } from '../../../common/Rbac';
import IconButton from 'rsuite/IconButton';
import { WhisperSpeaker } from '../../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import Whisper from 'rsuite/Whisper';
import { ConfirmModal } from '../../../common/confirm-modal';
import { NavLink } from 'react-router-dom';

type FormType = 'forApply' | 'forOrder';

const ConditionCell = ({
  rowData,
  dataKey,
  ...props
}: CellProps<CleanCustomsDeliveryTrueSignTariff>) => {
  let text = '';

  if (rowData?.fromAmount) {
    text = `от ${rowData?.fromAmount} `;
  }
  if (rowData?.toAmount) {
    text += `до ${rowData?.toAmount}`;
  }

  return rowData && <Table.Cell {...props}>{text}</Table.Cell>;
};

const RateCell = ({
  rowData,
  dataKey,
  ...props
}: CellProps<CleanCustomsDeliveryTrueSignTariff>) =>
  rowData && (
    <Table.Cell {...props}>
      {toMoney(rowData.rate)} {getCurrencySymbol('rub')}
    </Table.Cell>
  );

export const CustomsDeliveryTrueSignTab = () => {
  const { hasPermission } = useRbac();
  const [type, setType] = useState<FormType>('forApply');

  const input = { type: type };
  const { data, isLoading, mutate } = useSWR(
    ['customsDeliveryTrueSign.getList', input],
    () => trpc.customsDeliveryTrueSign.getList.query(input),
  );
  const { trigger: update } = useSWRMutation(
    ['customsDeliveryTrueSign.updateCustomsTrueSign'],
    (
      _k,
      {
        arg,
      }: {
        arg: RouterInput['customsDeliveryTrueSign']['updateCustomsTrueSign'];
      },
    ) => trpc.customsDeliveryTrueSign.updateCustomsTrueSign.mutate(arg),
  );
  const { trigger: deleteTariff } = useSWRMutation(
    ['customsDeliveryTrueSign.deleteCustomsTrueSign'],
    (
      _k,
      {
        arg,
      }: {
        arg: RouterInput['customsDeliveryTrueSign']['deleteCustomsTrueSign'];
      },
    ) => trpc.customsDeliveryTrueSign.deleteCustomsTrueSign.mutate(arg),
  );
  const canEdit = hasPermission(RbacObject.CustomsDelivery, 'write:settings');

  const renderMenu =
    (tariff: CleanCustomsDeliveryTrueSignTariff): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = async (eventKey: string | undefined) => {
        onClose();
        if (eventKey === '1') {
          ModalsService.createModal(CustomsDeliveryTrueSignEditModal, {
            defaultValues: {
              ...tariff,
              fromAmount: tariff.fromAmount || undefined,
              toAmount: tariff.toAmount || undefined,
            },
            title: 'Изменить тариф',
            onSubmit: async (data) => {
              await update({ id: tariff.id, data });
              mutate();
            },
          });
        }
        if (eventKey === '2') {
          ModalsService.createModal(ConfirmModal, {
            title: 'Удалить тариф',
            buttonText: 'Да',
          }).then(async () => {
            await deleteTariff({ id: tariff.id });
          });
        }
      };

      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={'1'}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>

            <Dropdown.Item eventKey={'2'}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<CleanCustomsDeliveryTrueSignTariff>) => {
    return (
      canEdit &&
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Rbac object={RbacObject.User} action={'write:admin'}>
            <Whisper
              placement="leftStart"
              trigger="click"
              speaker={renderMenu(rowData)}
            >
              <IconButton
                appearance="subtle"
                icon={<i className="bi bi-three-dots" />}
              />
            </Whisper>
          </Rbac>
        </Table.Cell>
      )
    );
  };

  return (
    <div>
      <div className={'d-flex gap-2 border-bottom border-gray-300 pb-1'}>
        <ul className="nav nav-pills d-inline-flex align-self-start mb-3">
          <li className="nav-item" onClick={() => setType('forApply')}>
            <NavLink
              to="/customs-deliveries/true-sign/for-apply"
              className="nav-link text-decoration-none"
            >
              Нанесение
            </NavLink>
          </li>
          <li className="nav-item" onClick={() => setType('forOrder')}>
            <NavLink
              to="/customs-deliveries/true-sign/for-order"
              className="nav-link text-decoration-none"
            >
              Заказ
            </NavLink>
          </li>
        </ul>
      </div>
      <Table
        loading={isLoading}
        data={data}
        headerHeight={57}
        rowKey="id"
        locale={{
          loading: 'Загрузка',
          emptyMessage: 'Нет тарифов',
        }}
        autoHeight
      >
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Условие</Table.HeaderCell>
          <ConditionCell />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Ставка</Table.HeaderCell>
          <RateCell />
        </Table.Column>
        <Table.Column flexGrow={0}>
          <Table.HeaderCell children="" />
          <ActionCell />
        </Table.Column>
      </Table>
    </div>
  );
};
